import { MICRO, toMicro } from './money';

/**
 * Purchase orders: goods that are on their way, and only become stock when an item receipt is made against them.
 *
 *   ordered             placed with the seller, not shipped yet
 *   in_transit          shipped, on its way
 *   partially_received  some of it has arrived and been receipted
 *   received            all of it has been receipted
 *   closed              part arrived and the rest is not coming (closed short)
 *   cancelled           nothing arrived and nothing will
 *
 * Purchases made by hand, with the goods already in hand, are `received` from the start.
 */
export const ORDER_STATUSES = ['ordered', 'in_transit', 'partially_received', 'received', 'closed', 'cancelled'] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

/** Statuses where pieces are still expected, so they count as "on order". */
export const OPEN_ORDER_STATUSES = ['ordered', 'in_transit', 'partially_received'] as const;
export const isOpenOrder = (status: string): boolean => (OPEN_ORDER_STATUSES as readonly string[]).includes(status);

export const ORDER_STATUS_LABEL: Record<OrderStatus, string> = {
  ordered: 'Ordered',
  in_transit: 'In transit',
  partially_received: 'Partly received',
  received: 'Fully received',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

/** What a person can do to an order's status. Receiving goods moves it on by itself. */
export const ORDER_ACTIONS = ['mark_in_transit', 'close', 'cancel'] as const;
export type OrderAction = (typeof ORDER_ACTIONS)[number];

// ---------------------------------------------------------------- BrickLink order statuses

/**
 * BrickLink's status for an order you placed, as ours. BrickLink's own list: PENDING, UPDATED, PROCESSING, READY,
 * PAID, PACKED, SHIPPED, RECEIVED, COMPLETED, OCR (cancel requested), NPB, NPX, NRS, NSS (the order fell
 * through) and CANCELLED. Written from memory of the documentation, so anything unfamiliar is treated as still
 * on order rather than guessed at.
 *
 * `arrived` is BrickLink saying you have the goods. It never receipts anything: only a receipt in BrickLoop does.
 */
export function fromBricklinkOrderStatus(status: string | null | undefined): { status: 'ordered' | 'in_transit' | 'cancelled'; arrived: boolean } {
  const s = (status ?? '').trim().toUpperCase();
  if (['CANCELLED', 'CANCELED', 'NPB', 'NPX', 'NRS', 'NSS'].includes(s)) return { status: 'cancelled', arrived: false };
  if (s === 'SHIPPED') return { status: 'in_transit', arrived: false };
  if (s === 'RECEIVED' || s === 'COMPLETED') return { status: 'in_transit', arrived: true };
  return { status: 'ordered', arrived: false };
}

// ---------------------------------------------------------------- landed cost

export interface CostLine {
  id: number;
  qty: number;
  /** Price of one piece in the order's currency. */
  unitPrice: string | number;
}

export interface LandedCostLine {
  id: number;
  /** What one piece really cost: its price plus its share of postage and other charges, six places. */
  unitCost: string;
  /** What the whole line cost, in micro units (four places). */
  totalMicro: number;
}

export interface LandedCosts {
  lines: LandedCostLine[];
  /** The lines' prices added up, in micro units. */
  itemsMicro: number;
  /** What was paid beyond the items (postage, insurance, charges, tax, less any discount), in micro units. Can be negative. */
  extrasMicro: number;
}

/**
 * Works out what each piece of an order cost once postage, insurance, other charges, tax and discounts are spread
 * over it, in proportion to what each line is worth (a 3p part and a 15 pound minifigure do not share postage
 * equally). Shares are rounded with the largest remainder method, so the lines add up to the amount paid exactly.
 *
 * `totalMicro` is everything paid. `recoverableTaxMicro` is VAT you can claim back, which is not a cost of the
 * stock, so it comes off before spreading. If every line is free, the extras are spread equally per piece.
 */
export function landedCosts(lines: CostLine[], totalMicro: number, recoverableTaxMicro = 0): LandedCosts {
  const usable = lines.filter((l) => l.qty > 0);
  const priced = usable.map((l) => {
    const price = toMicro(l.unitPrice) ?? 0;
    return { line: l, itemsMicro: price * l.qty };
  });
  const itemsMicro = priced.reduce((s, p) => s + p.itemsMicro, 0);
  const toSpread = Math.max(0, totalMicro - recoverableTaxMicro);
  const extrasMicro = toSpread - itemsMicro;

  const weights = itemsMicro > 0 ? priced.map((p) => p.itemsMicro) : priced.map((p) => p.line.qty);
  const weightSum = weights.reduce((a, b) => a + b, 0);

  const exact = weights.map((w) => (weightSum > 0 ? (extrasMicro * w) / weightSum : 0));
  const shares = exact.map(Math.floor);
  let remainder = extrasMicro - shares.reduce((a, b) => a + b, 0);
  if (weightSum > 0) {
    const order = exact
      .map((x, i) => ({ i, frac: x - Math.floor(x) }))
      .sort((a, b) => b.frac - a.frac || a.i - b.i);
    for (const { i } of order) {
      if (remainder <= 0) break;
      shares[i]! += 1;
      remainder -= 1;
    }
  }

  return {
    itemsMicro,
    extrasMicro,
    lines: priced.map((p, i) => {
      const totalLine = Math.max(0, p.itemsMicro + shares[i]!);
      return { id: p.line.id, totalMicro: totalLine, unitCost: (totalLine / MICRO / p.line.qty).toFixed(6) };
    }),
  };
}

/** Converts a cost in the order's currency into the default currency. A rate of null means "same currency". */
export function convertCost(unitCost: string, rate: string | number | null): string {
  if (rate === null) return unitCost;
  return (Number(unitCost) * Number(rate)).toFixed(6);
}
